import { MessageDecoder, MessageEncoder, TrackReferenceOrPlaceholder, WidgetState } from '@livekit/components-core';
// import { MessageDecoder } from '@livekit/components-core';
import { isEqualTrackRef, isTrackReference, isWeb, log } from '@livekit/components-core';
import { RoomEvent, Track } from 'livekit-client';
import * as React from 'react';
import {
  MessageFormatter,
  CarouselLayout,
  ConnectionStateToast,
  FocusLayout,
  FocusLayoutContainer,
  GridLayout,
  LayoutContextProvider,
  ParticipantTile,
  useTracks,
  useCreateLayoutContext,
  usePinnedTracks,
  Chat,
} from '@livekit/components-react';
import { ViewerControlBar } from './components/ViewerControlBar/ViewerControlBar';
import { isHostParticipantMetadata } from './viewerLivestreamState';
import classes from './ViewerConference.module.css';

export interface ViewerConferenceProps extends React.HTMLAttributes<HTMLDivElement> {
  chatMessageFormatter?: MessageFormatter;
  chatMessageEncoder?: MessageEncoder;
  chatMessageDecoder?: MessageDecoder;
  defaultWidgetState?: WidgetState;
}

export function ViewerConference({
  chatMessageFormatter,
  chatMessageDecoder,
  chatMessageEncoder,
  defaultWidgetState,
  className,
  ...props
}: ViewerConferenceProps) {
  const [widgetState, setWidgetState] = React.useState<WidgetState>({
    showChat: true,
    unreadMessages: 0,
    showSettings: true,
  });
  const lastAutoFocusedScreenShareTrack = React.useRef<TrackReferenceOrPlaceholder | null>(null);

  const tracks = useTracks(
    [
      { source: Track.Source.Camera, withPlaceholder: true },
      { source: Track.Source.ScreenShare, withPlaceholder: false },
    ],
    { updateOnlyOn: [RoomEvent.ActiveSpeakersChanged], onlySubscribed: false },
  );

  const widgetUpdate = (state: WidgetState) => {
    setWidgetState(state);
  };

  const layoutContext = useCreateLayoutContext();

  const screenShareTracks = tracks
    .filter(isTrackReference)
    .filter((track) => track.publication.source === Track.Source.ScreenShare);

  const hostTracks = tracks.filter((track) => isHostParticipantMetadata(track.participant.metadata));
  const focusTrack = usePinnedTracks(layoutContext)?.[0];
  const carouselTracks = hostTracks.filter((track) => !isEqualTrackRef(track, focusTrack));

  React.useEffect(() => {
    // If screen share tracks are published, and no pin is set explicitly, auto set the screen share.
    if (
      screenShareTracks.some((track) => track.publication.isSubscribed) &&
      lastAutoFocusedScreenShareTrack.current === null
    ) {
      log.debug('Auto set screen share focus:', { newScreenShareTrack: screenShareTracks[0] });
      layoutContext.pin.dispatch?.({ msg: 'set_pin', trackReference: screenShareTracks[0] });
      lastAutoFocusedScreenShareTrack.current = screenShareTracks[0];
    } else if (
      lastAutoFocusedScreenShareTrack.current &&
      !screenShareTracks.some(
        (track) => track.publication.trackSid === lastAutoFocusedScreenShareTrack.current?.publication?.trackSid,
      )
    ) {
      log.debug('Auto clearing screen share focus.');
      layoutContext.pin.dispatch?.({ msg: 'clear_pin' });
      lastAutoFocusedScreenShareTrack.current = null;
    }
    if (focusTrack && !isTrackReference(focusTrack)) {
      const updatedFocusTrack = tracks.find(
        (tr) => tr.participant.identity === focusTrack.participant.identity && tr.source === focusTrack.source,
      );
      if (updatedFocusTrack !== focusTrack && isTrackReference(updatedFocusTrack)) {
        layoutContext.pin.dispatch?.({ msg: 'set_pin', trackReference: updatedFocusTrack });
      }
    }
  }, [
    screenShareTracks.map((ref) => `${ref.publication.trackSid}_${ref.publication.isSubscribed}`).join(),
    focusTrack?.publication?.trackSid,
    tracks,
  ]);

  React.useEffect(() => {
    if (defaultWidgetState && defaultWidgetState.showChat !== undefined) {
      layoutContext.widget.dispatch?.({
        msg: defaultWidgetState.showChat ? 'show_chat' : 'hide_chat',
      });
    }
  }, [defaultWidgetState]);

  return (
    <div className={`lk-video-conference ${classes.conference} ${className ?? ''}`} {...props}>
      {isWeb() && (
        <LayoutContextProvider value={layoutContext} onWidgetChange={widgetUpdate}>
          <div className={classes.layout}>
            <div className={`lk-video-conference-inner ${classes.videoPane}`}>
              {!focusTrack ? (
                <div className="lk-grid-layout-wrapper">
                  <GridLayout tracks={hostTracks}>
                    <ParticipantTile />
                  </GridLayout>
                </div>
              ) : (
                <div className="lk-focus-layout-wrapper">
                  <FocusLayoutContainer>
                    <CarouselLayout tracks={carouselTracks}>
                      <ParticipantTile />
                    </CarouselLayout>
                    {focusTrack && <FocusLayout trackRef={focusTrack} />}
                  </FocusLayoutContainer>
                </div>
              )}
              <ViewerControlBar controls={{ chat: true }} />
            </div>
            <div className={classes.chatPane} hidden={!widgetState.showChat}>
              <Chat
                messageFormatter={chatMessageFormatter}
                messageEncoder={chatMessageEncoder}
                messageDecoder={chatMessageDecoder}
              />
            </div>
          </div>
        </LayoutContextProvider>
      )}
      <ConnectionStateToast />
    </div>
  );
}
